'use client'

import { IdiomMetadata, IdiomCharacter } from '@/content/idioms'
import { useSettings } from '@/hooks/useSettings'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { BookOpen, ArrowLeft } from 'lucide-react'
import { Link } from '@/i18n/routing'

type IdiomDetailClientProps = {
  idiom: IdiomMetadata
}

export function IdiomDetailClient({ idiom }: IdiomDetailClientProps) {
  const { settings } = useSettings()
  const t = useTranslations('idioms')

  const getDisplayScript = (text: { s: string; t: string }) => {
    return settings.scriptPriority === 'traditional' ? text.t : text.s
  }

  const getCharScript = (char: IdiomCharacter) => {
    return settings.scriptPriority === 'traditional' ? char.t : char.s
  }

  const getPronunciation = (pinyin: string, zhuyin: string) => {
    return settings.pronunciationPriority === 'zhuyin' ? zhuyin : pinyin
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Back button */}
      <Button asChild variant="ghost" className="-ml-2 w-fit">
        <Link href="/read/idioms">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('backToIdioms')}
        </Link>
      </Button>

      {/* Hero section with idiom display */}
      <Card className="border-border/70 overflow-hidden rounded-3xl border shadow-sm">
        {/* Topics/Tags */}
        {idiom.tags && idiom.tags.length > 0 && (
          <div className="flex justify-end gap-2 p-6 pb-0">
            {idiom.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Main idiom display */}
        <div className="p-6 sm:p-8">
          <h1
            className={cn(
              'mb-3 text-center text-4xl font-bold tracking-tight sm:text-5xl',
              settings.scriptPriority === 'traditional'
                ? 'font-chinese-tc'
                : 'font-chinese-sc'
            )}
          >
            {getDisplayScript(idiom.idiom)}
          </h1>

          <p className="text-muted-foreground mb-6 text-center text-lg">
            {getPronunciation(idiom.pinyin, idiom.zhuyin)}
          </p>

          {/* Character breakdown */}
          <div className="mb-6 grid grid-cols-4 gap-2 sm:gap-4">
            {idiom.characters.map((char, index) => (
              <Link
                key={index}
                href={`/dict?q=${encodeURIComponent(getCharScript(char))}`}
                className="bg-muted/50 hover:bg-muted/80 flex flex-col items-center rounded-xl p-3 transition-colors sm:p-4"
              >
                <span
                  className={cn(
                    'mb-1 text-2xl font-bold sm:text-3xl',
                    settings.scriptPriority === 'traditional'
                      ? 'font-chinese-tc'
                      : 'font-chinese-sc'
                  )}
                >
                  {getCharScript(char)}
                </span>
                <span className="text-muted-foreground mb-1 text-xs sm:text-sm">
                  {getPronunciation(char.pinyin, char.zhuyin)}
                </span>
                <span className="text-center text-xs text-amber-600 dark:text-amber-400">
                  {char.meaning}
                </span>
              </Link>
            ))}
          </div>

          {/* Literal meaning */}
          <div className="mb-4">
            <h2 className="text-muted-foreground mb-1 text-sm font-medium tracking-wide uppercase">
              {t('literalMeaning')}
            </h2>
            <p className="text-lg italic">
              &ldquo;{idiom.literalMeaning}&rdquo;
            </p>
          </div>

          {/* Actual meaning */}
          <div className="mb-6">
            <h2 className="text-muted-foreground mb-1 text-sm font-medium tracking-wide uppercase">
              {t('meaning')}
            </h2>
            <p className="text-lg">{idiom.meaning}</p>
          </div>

          {/* Origin story */}
          {idiom.story && (
            <div className="bg-muted/30 rounded-xl p-4 sm:p-6">
              <div className="mb-2 flex items-center gap-2">
                <BookOpen className="text-primary h-5 w-5" />
                <h2 className="font-semibold">{t('story')}</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {idiom.story}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
